import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react'
import { InputGroup, FormControl } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import ROUTES from '../../const/routes'
import { hasFormError } from '../../helpers/formValidation'
import strings from '../../locale/en'
import { handleResetPassword } from '../../redux/user/actions'
import validateResetPasswordForm from './helper'
import {
  ResetPasswordWrapper,
  Container,
  Title,
  SubTitle,
  FormContainer,
  ResetButton,
  BackToLoginLink,
  ChevronLeftIcon,
  EyeIcon
} from './subcomponents'
import { useSelector, shallowEqual } from 'react-redux'
import { RootState } from '../../redux/store'
import { ROLE } from '../../helpers/determineMenu'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { PasswordDetails, ResetPasswordField } from './typing'

const ResetPassword = () => {
  const { isLoggedIn, userInfo } = useSelector(
    (state: RootState) => state.user,
    shallowEqual
  )

  const initialValues: PasswordDetails = {
    password: '',
    confirmPassword: ''
  }

  const {
    resetPassword: { title, description, submit, backToLogin }
  } = strings

  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const isResetPasswordRequired = location.state

  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState({} as Record<string, string>)
  const [fields, setfields] = useState({
    showPassword: false,
    showConfirmPassword: false
  })

  const togglePasswordVisibility = (type: string) =>
    type === 'password'
      ? setfields({ ...fields, showPassword: !fields.showPassword })
      : setfields({
          ...fields,
          showConfirmPassword: !fields.showConfirmPassword
        })

  const handleBlur = (field: ResetPasswordField): void => {
    setErrors(
      validateResetPasswordForm({
        values,
        errors,
        field
      })
    )
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    const validationError = validateResetPasswordForm({
      values,
      errors
    })

    if (hasFormError(validationError)) {
      setErrors(validationError)
    } else {
      dispatch(handleResetPassword(values.password))
    }
  }

  useEffect(() => {
    if (isLoggedIn) {
      switch (userInfo?.role) {
        case ROLE.STUDENT:
          history.push(ROUTES.LEARN)
          break
        case ROLE.ADMIN:
          history.push(ROUTES.DASHBOARD)
          break
        case ROLE.INSTITUTE_ADMIN:
          history.push(ROUTES.BRANCHADMIN)
          break
        case ROLE.TEACHER_ADMIN:
          history.push(ROUTES.PUBLISH_LIST)
          break
        default:
          history.push(ROUTES.TEACHER)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  return !isResetPasswordRequired ? (
    <Redirect to={ROUTES.LOGIN} />
  ) : (
    <ResetPasswordWrapper>
      <Container>
        <Title>{title}</Title>
        <SubTitle>{description}</SubTitle>
        <FormContainer noValidate onSubmit={handleSubmit}>
          <InputGroup className="mb-3">
            <FormControl
              type={fields.showPassword ? 'text' : 'password'}
              name="password"
              value={values.password}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, password: e.target.value })
              }
              onBlur={() => handleBlur('password')}
              placeholder="Password"
              isValid={!errors.password}
              isInvalid={!!errors.password}
            />
            {values.password && (
              <EyeIcon
                icon={
                  fields.showPassword ? ['far', 'eye-slash'] : ['far', 'eye']
                }
                size="1x"
                onClick={() => togglePasswordVisibility('password')}
              />
            )}
            <FormControl.Feedback type="invalid">
              {errors.password}
            </FormControl.Feedback>
          </InputGroup>
          <InputGroup className="mb-3">
            <FormControl
              type={fields.showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={values.confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, confirmPassword: e.target.value })
              }
              onBlur={() => handleBlur('confirmPassword')}
              placeholder="Confirm Password"
              isValid={!errors.confirmPassword}
              isInvalid={!!errors.confirmPassword}
            />
            {values.confirmPassword && (
              <EyeIcon
                icon={
                  fields.showConfirmPassword
                    ? ['far', 'eye-slash']
                    : ['far', 'eye']
                }
                size="1x"
                onClick={() => togglePasswordVisibility('confirmPassword')}
              />
            )}
            <FormControl.Feedback type="invalid">
              {errors.confirmPassword}
            </FormControl.Feedback>
          </InputGroup>
          <ResetButton
            disabled={hasFormError(errors)}
            className="mb-3 mt-3"
            variant="primary"
            type="submit"
          >
            {submit}
          </ResetButton>
          <BackToLoginLink to={ROUTES.LOGIN}>
            <ChevronLeftIcon icon={['fas', 'angle-double-left']} size="1x" />
            {backToLogin}
          </BackToLoginLink>
        </FormContainer>
      </Container>
    </ResetPasswordWrapper>
  )
}

export default ResetPassword
